/**
 * TokenPocket (TP Wallet) helpers.
 * TP injects a MetaMask-compatible EIP-1193 provider at window.ethereum
 * (also window.tokenpocket.ethereum) flagged `isTokenPocket`, both in its
 * mobile DApp browser and its browser extension.
 * Docs: https://help.tokenpocket.pro/developer-en
 */
export const TP_DOWNLOAD_URL = 'https://www.tokenpocket.pro/en/download/app';
export const TP_EXTENSION_URL = 'https://extension.tokenpocket.pro/';
export const TP_SESSION_KEY = 'wallet_brand';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyProvider = any;

/** TP's injected provider, or undefined when the page is not running inside TokenPocket. */
export function getTokenPocketProvider(): AnyProvider | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as AnyProvider;
  if (w.tokenpocket?.ethereum) return w.tokenpocket.ethereum;
  const eth = w.ethereum;
  if (!eth) return undefined;
  if (Array.isArray(eth.providers)) {
    const tp = eth.providers.find((p: AnyProvider) => p?.isTokenPocket);
    if (tp) return tp;
  }
  return eth.isTokenPocket ? eth : undefined;
}

export function isTokenPocketInstalled(): boolean {
  return Boolean(getTokenPocketProvider());
}

/** When several wallets are injected (MetaMask + TP extension), make TP the active one. */
export function selectTokenPocketProvider(): void {
  const w = window as AnyProvider;
  const tp = getTokenPocketProvider();
  if (tp && w.ethereum?.providers && typeof w.ethereum.setSelectedProvider === 'function') {
    w.ethereum.setSelectedProvider(tp);
  }
}

/** Deep link that opens `url` inside TokenPocket's mobile DApp browser. */
export function getTokenPocketDeepLink(url: string = window.location.href): string {
  const params = { url, chain: 'Polygon', source: 'decarb.frac.io' };
  return `tpdapp://open?params=${encodeURIComponent(JSON.stringify(params))}`;
}

/** Open the current page in the TP app; if TP is not installed the page stays visible, so go to the download page. */
export function openInTokenPocket(): void {
  const started = Date.now();
  window.location.href = getTokenPocketDeepLink();
  window.setTimeout(() => {
    if (!document.hidden && Date.now() - started < 2500) {
      window.location.assign(TP_DOWNLOAD_URL);
    }
  }, 2000);
}

export function markTokenPocketSession(): void {
  try { localStorage.setItem(TP_SESSION_KEY, 'tokenpocket'); } catch (e) { /* ignore */ }
}
export function clearTokenPocketSession(): void {
  try { localStorage.removeItem(TP_SESSION_KEY); } catch (e) { /* ignore */ }
}
export function isTokenPocketSession(): boolean {
  try { return localStorage.getItem(TP_SESSION_KEY) === 'tokenpocket'; } catch (e) { return false; }
}
